import type { ApolloCache } from '@apollo/client';
import type { ApolloLink } from '@apollo/client/link';
import { useMutation, useQuery } from '@apollo/client/react';
import { Col } from '@opendoor/atoms/box';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Navigate } from 'react-router';

import { UpdatePartnerBranchMemberDocument } from '../graphql/mutations/__generated__/UpdatePartnerBranchMember';
import type { UpdatePartnerBranchMember } from '../graphql/mutations/__generated__/UpdatePartnerBranchMember';
import { GetPartnerBranchesDocument } from '../graphql/queries/__generated__/GetPartnerBranches';
import type { GetPartnerBranches } from '../graphql/queries/__generated__/GetPartnerBranches';

type GetPartnerBranches_currentPartnerBranch = NonNullable<
  GetPartnerBranches['currentPartnerBranch']
>;
import { formatPhoneNumberInput, sortRegionsByDisplayName } from '../helpers/formatUtils';
import { ERROR_PAGE_ROUTE } from '../routes/routes';
import ContactsSection from './ContactsSection';
import { Loader } from './Loader';

const ContactsPanel = () => {
  const updateCache = (
    cache: ApolloCache,
    { data }: ApolloLink.Result<UpdatePartnerBranchMember>,
  ) => {
    const partnerBranchesCache: GetPartnerBranches | null = cache.readQuery({
      query: GetPartnerBranchesDocument,
    });

    if (partnerBranchesCache && data?.updatePartnerBranchMember) {
      cache.writeQuery({
        query: GetPartnerBranchesDocument,
        data: {
          partnerBranches: partnerBranchesCache.partnerBranches,
          currentPartnerBranch: data.updatePartnerBranchMember,
        },
      });
    }
  };

  const [updateMutation, { loading: mutationLoading, error: mutationError }] = useMutation(
    UpdatePartnerBranchMemberDocument,
    { update: updateCache },
  );

  const renderContactsSection = (currentBranch: GetPartnerBranches_currentPartnerBranch | null) => {
    if (currentBranch && currentBranch.partnerManagerContact) {
      return (
        <ContactsSection
          contacts={[
            {
              email: currentBranch.partnerManagerContact.email ?? '',
              name: currentBranch.partnerManagerContact.fullName ?? '',
              phone: currentBranch.partnerManagerContact.phoneNumber
                ? formatPhoneNumberInput(currentBranch.partnerManagerContact.phoneNumber)
                : '',
            },
          ]}
          title="Your Opendoor Contact"
        />
      );
    }
  };

  const { loading: queryLoading, error: queryError, data } = useQuery(GetPartnerBranchesDocument);
  if (queryLoading) {
    return <Loader size="small" />;
  }

  if (queryError) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }

  const handleDropdownChange = (value: string) => {
    if (
      value &&
      (!data!.currentPartnerBranch ||
        (data!.currentPartnerBranch && value !== data!.currentPartnerBranch!.branchId))
    ) {
      updateMutation({ variables: { branchID: value } });
    }
  };

  const options = data!.partnerBranches
    .filter((branch) => {
      return branch.marketActive;
    })
    .map((branch) => ({
      value: branch.branchId,
      label: branch.marketDisplayName,
    }))
    .sort(sortRegionsByDisplayName);

  const selectedBranchId = data!.currentPartnerBranch?.branchId ?? '';
  const selectedOption = options.find((option) => option.value === selectedBranchId) ?? null;

  return (
    <Col>
      <Field label="Select Your Market" id="market-selector">
        <Combobox
          id="market-selector"
          options={options}
          value={selectedOption}
          onValueChange={(option) => handleDropdownChange(option?.value ?? '')}
          analyticsName="contacts-panel-market-dropdown"
        />
      </Field>
      <Col className="pt-3">
        {mutationLoading ? (
          <Loader size="small" />
        ) : (
          renderContactsSection(data!.currentPartnerBranch)
        )}
        {mutationError && <Navigate to={ERROR_PAGE_ROUTE} replace />}
      </Col>
    </Col>
  );
};

export default ContactsPanel;
